import * as React from 'react';
import { Theme } from '@mui/material';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import Dialog from '@mui/material/Dialog';
import * as Device from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useReactiveVar } from '@apollo/client';

import { conversationDialogOpenVar, conversationIdVar, conversationOpenListVar } from '@context';
import { AuthenticationDialog } from '@components';
import { useSession } from '@hooks';
import { AccountButton, AccountDrawer } from './account';
import { PublicButton, PublicDrawer } from './public';
import { ConversationButton, ConversationDrawer, ConversationDialog } from './conversation';
import { NotificationButton, NotificationDrawer } from './notification';
import { AccountSettingsDialog, NotificationSettingsDialog, UserSettingsDialog } from './account/dialog';

type DrawerType = 'public' | 'inbox' | 'account' | 'notification';

export default function AccountHeader() {
  const { user } = useSession();
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(null);
  const [drawer, setDrawer] = React.useState<DrawerType | null>(null);
  const [openDialog, setOpenDialog] = React.useState(false);
  const [dialogMenu, setDialogMenu] = React.useState<string | null>(null);
  const isConversationDialogOpen = useReactiveVar(conversationDialogOpenVar);
  const conversationId = useReactiveVar(conversationIdVar);
  const { t } = useTranslation('account');

  const handleOpenDrawer = (event: React.MouseEvent<HTMLElement>) => {
    const dataId = event.currentTarget.getAttribute('data-id');
    setDrawer(dataId as DrawerType | null);
    setAnchorElUser(event.currentTarget);
    // important to inform that the conversation list drawer is open or not
    // it will be used to pause push notifications for conversations when the list is open
    conversationOpenListVar(dataId === 'inbox');
  };

  const handleCloseDrawer = () => {
    setAnchorElUser(null);
    // important to inform that the conversation list drawer is open or not
    // it will be used to pause push notifications for conversations when the list is open
    conversationOpenListVar(false);
  };

  const handleOpenDialog = (event: React.MouseEvent<HTMLElement>) => {
    const dataId = event.currentTarget.getAttribute('data-id');
    const conversationId = event.currentTarget.getAttribute('conversation-id');

    if (drawer === 'inbox') {
      conversationIdVar(conversationId || null);
      conversationDialogOpenVar(true);
    }

    setDialogMenu(dataId);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    if (conversationIdVar()) {
      conversationIdVar(null);
    }
    if (conversationDialogOpenVar()) {
      conversationDialogOpenVar(false);
    }
  };

  const renderDialog = () => {
    if (drawer === 'inbox' || isConversationDialogOpen) {
      return <ConversationDialog handleCloseDialog={handleCloseDialog} />;
    } else if (drawer === 'account') {
      switch (dialogMenu) {
        case 'account-settings':
          return <AccountSettingsDialog handleCloseDialog={handleCloseDialog} />;
        case 'user-settings':
          return (
            <UserSettingsDialog handleCloseDialog={handleCloseDialog} handleCloseDrawer={handleCloseDrawer} />
          );
        case 'notification-settings':
          return <NotificationSettingsDialog handleCloseDialog={handleCloseDialog} />;
        default:
          return null;
      }
    }
  };

  const renderDrawer = () => {
    switch (drawer) {
      case 'public':
        return <PublicDrawer handleCloseDrawer={handleCloseDrawer} />;
      case 'inbox':
        return (
          <ConversationDrawer handleOpenDialog={handleOpenDialog} handleCloseDrawer={handleCloseDrawer} />
        );
      case 'notification':
        return (
          <NotificationDrawer handleOpenDialog={handleOpenDialog} handleCloseDrawer={handleCloseDrawer} />
        );
      case 'account':
        return <AccountDrawer handleOpenDialog={handleOpenDialog} handleCloseDrawer={handleCloseDrawer} />;
      default:
        return null;
    }
  };

  React.useEffect(() => {
    if (!user && anchorElUser) {
      setAnchorElUser(null);
    }
  }, [user]);

  return (
    <>
      {!user && (
        <>
          <AuthenticationDialog
            button={
              <Button variant="contained" size="small" sx={{ ml: 'auto' }}>
                {t('connection', { ns: 'cta' })}
              </Button>
            }
          />
          <PublicButton handleOpenDrawer={handleOpenDrawer} />
        </>
      )}
      {user && (
        <Stack marginLeft="auto" direction="row" justifyContent="flex-end" alignItems="center" spacing={1}>
          <NotificationButton handleOpenDrawer={handleOpenDrawer} />
          <ConversationButton handleOpenDrawer={handleOpenDrawer} />
          <AccountButton handleOpenDrawer={handleOpenDrawer} />
        </Stack>
      )}
      <Drawer
        anchor={'right'}
        open={!!anchorElUser}
        onClose={handleCloseDrawer}
        sx={{
          zIndex: (theme: Theme) => theme.zIndex.drawer + 2,
        }}
        slotProps={{
          paper: {
            sx: {
              height: '100vh',
              pt: Device.isMobile && !Device.isBrowser ? 'env(safe-area-inset-top)' : undefined,
            },
          },
        }}
      >
        {renderDrawer()}
      </Drawer>
      <Dialog
        onClose={handleCloseDialog}
        open={openDialog || isConversationDialogOpen}
        scroll="paper"
        fullScreen={Device.isMobileOnly}
        fullWidth
        maxWidth="sm"
        slotProps={{
          paper: {
            sx: {
              overflowY: 'hidden',
              height: Device.isDesktop ? 'calc(100dvh - 70px)' : undefined,
              pb: 0,
            },
          },
        }}
      >
        {renderDialog()}
      </Dialog>
    </>
  );
}
